/**
 * Helper to check if a room should be included in business credits calculation.
 *
 * Returns true if:
 * - Room allocation has NOT been done yet (no employeeIds AND no guestIds) - assume employees will use the room
 * - Room has at least one employee assigned
 *
 * Returns false if:
 * - Room allocation has been done AND room has ONLY guest travellers (guestIds present but no employeeIds)
 *
 * Note on "pending allocation" state:
 * When neither employeeIds nor guestIds are present, we optimistically assume employees will use the room.
 * This is safe because:
 * 1. This function is called from memoized Redux selectors (e.g., selectToBeCalculatedCheckoutBusinessOfferCreditItems)
 * 2. When occupancy changes in Redux state, the selector chain automatically recalculates
 * 3. If allocation completes with only guests, the selectors will recompute and credits will update accordingly
 */
export function roomHasEmployeeTravellers(itemView: App.Checkout.AccommodationItemView): boolean {
  const occupancy = itemView.item.occupancy
  if (!occupancy) {
    // No occupancy data - include in credits (default behavior for pending allocation)
    return true
  }

  const employeeIds = occupancy.employeeIds
  const guestIds = occupancy.guestIds

  const hasEmployees = Array.isArray(employeeIds) && employeeIds.some(id => id && id.trim().length > 0)
  const hasGuests = Array.isArray(guestIds) && guestIds.length > 0

  // If room allocation hasn't been done (no employees AND no guests assigned), include the room.
  // This is an optimistic default - assume employees will use the room until allocation is complete.
  // Recalculation happens automatically via Redux selector dependencies when occupancy state changes.
  if (!hasEmployees && !hasGuests) {
    return true
  }

  return hasEmployees
}
